import { projectApi } from '@/lib/api/project';
import type { Project, ProjectCreate, ProjectUpdate } from "dico-types";
import { create } from 'zustand';

export type ProjectsByWorkspaceId = Record<string, Project[]>;

interface ProjectStoreState {
  /** Projects keyed by workspace id. */
  projectsByWorkspaceId: ProjectsByWorkspaceId;
  /** Currently selected project ID (null = all projects/global view) */
  selectedProjectId: string | null;
  /** Fetch projects for a workspace */
  fetchProjects: (workspaceId: string) => Promise<void>;
  /** Get projects for a workspace (from store) */
  getProjects: (workspaceId: string) => Project[];
  /** Create a new project */
  createProject: (workspaceId: string, data: Omit<ProjectCreate, 'workspaceId'>) => Promise<Project>;
  /** Update a project */
  updateProject: (workspaceId: string, projectId: string, data: ProjectUpdate) => Promise<Project>;
  /** Delete a project */
  deleteProject: (workspaceId: string, projectId: string) => Promise<void>;
  /** Set the currently selected project */
  setSelectedProject: (projectId: string | null) => void;
  /** Reset the store */
  reset: () => void;
}

export const useProjectStore = create<ProjectStoreState>((set, get) => ({
  projectsByWorkspaceId: {},
  selectedProjectId: null,

  fetchProjects: async (workspaceId: string) => {
    try {
      const res = await projectApi.getProjects(workspaceId);
      const projects = res.data ?? [];
      set((s) => ({
        projectsByWorkspaceId: { ...s.projectsByWorkspaceId, [workspaceId]: projects }
      }));
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      // Keep existing data on error
    }
  },

  getProjects: (workspaceId: string) => {
    return get().projectsByWorkspaceId[workspaceId] ?? [];
  },

  createProject: async (workspaceId: string, data: Omit<ProjectCreate, 'workspaceId'>) => {
    const res = await projectApi.createProject(workspaceId, data);
    if (!res.success || !res.data) {
      throw new Error(res.error ?? 'Failed to create project');
    }
    const project = res.data;
    set((s) => ({
      projectsByWorkspaceId: {
        ...s.projectsByWorkspaceId,
        [workspaceId]: [...(s.projectsByWorkspaceId[workspaceId] ?? []), project]
      }
    }));
    return project;
  },

  updateProject: async (workspaceId: string, projectId: string, data: ProjectUpdate) => {
    const res = await projectApi.updateProject(workspaceId, projectId, data);
    if (!res.success || !res.data) {
      throw new Error(res.error ?? 'Failed to update project');
    }
    const project = res.data;
    set((s) => ({
      projectsByWorkspaceId: {
        ...s.projectsByWorkspaceId,
        [workspaceId]: (s.projectsByWorkspaceId[workspaceId] ?? []).map((p) =>
          p.id === projectId ? project : p
        )
      }
    }));
    return project;
  },

  deleteProject: async (workspaceId: string, projectId: string) => {
    const res = await projectApi.deleteProject(workspaceId, projectId);
    if (!res.success) {
      throw new Error(res.error ?? 'Failed to delete project');
    }
    set((s) => ({
      projectsByWorkspaceId: {
        ...s.projectsByWorkspaceId,
        [workspaceId]: (s.projectsByWorkspaceId[workspaceId] ?? []).filter((p) => p.id !== projectId)
      },
      // Clear selected project if it was deleted
      selectedProjectId: s.selectedProjectId === projectId ? null : s.selectedProjectId
    }));
  },

  setSelectedProject: (projectId: string | null) => {
    set({ selectedProjectId: projectId });
  },

  reset: () => set({ projectsByWorkspaceId: {}, selectedProjectId: null })
}));
